import { useMutation, useQueryClient } from '@tanstack/react-query';
import { privateApi } from '../../../api/api';

// Impor skema dan tipe yang relevan
// Respons sukses dari API delete adalah 'data: null', jadi kita buat tipe sederhana
import { z } from 'zod';

// Skema untuk memvalidasi respons sukses dari operasi DELETE
const DeleteUserV4ResponseSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    code: z.number(),
    data: z.null(), // API mengembalikan data: null saat sukses
});

type DeleteUserV4Response = z.infer<typeof DeleteUserV4ResponseSchema>;

const API_BASE_ENDPOINT = '/v4/user';

/**
 * Hook kustom untuk menghapus pengguna (Versi 4).
 * Mengenkapsulasi logika mutasi, termasuk pemanggilan API
 * dan invalidasi cache.
 * 
 * @returns Hasil dari React Query `useMutation`.
 */
export const useDeleteUserV4 = () => {
    const queryClient = useQueryClient();

    return useMutation<DeleteUserV4Response, Error, string>({
        /**
         * Fungsi mutasi yang akan dijalankan saat `mutate()` dipanggil.
         * @param userId - ID dari pengguna yang akan dihapus.
         */
        mutationFn: async (userId: string): Promise<DeleteUserV4Response> => {
            if (!userId) {
                throw new Error("User ID is required to delete a user.");
            }

            try {
                // Melakukan request DELETE ke endpoint spesifik pengguna.
                const response = await privateApi.delete(`${API_BASE_ENDPOINT}/${userId}`);

                // Validasi struktur data respons dari API.
                const validationResult = DeleteUserV4ResponseSchema.safeParse(response.data);

                if (!validationResult.success) {
                    console.error(`API response validation failed in useDeleteUserV4 for user ID ${userId}:`, validationResult.error.flatten());
                    throw new Error("Invalid data format received from the server after deleting user.");
                }

                return validationResult.data;
            } catch (error) {
                console.error(`Failed to delete user (V4) with ID ${userId}:`, error);
                throw error;
            }
        },

        /**
         * Callback yang dijalankan setelah mutasi berhasil.
         * 
         * @param _data - Data yang dikembalikan dari `mutationFn`.
         * @param userId - Variabel yang dikirim ke `mutationFn`.
         */
        onSuccess: (_data, userId) => {
            // 1. Invalidate semua query yang berhubungan dengan daftar pengguna.
            // Ini akan membuat tabel pengguna diperbarui.
            queryClient.invalidateQueries({ queryKey: ['users', 'v4', 'list'] });

            // 2. Hapus cache untuk detail pengguna yang baru saja dihapus.
            // Ini berguna untuk memastikan data yang sudah dihapus tidak muncul lagi
            // dari cache jika pengguna mencoba mengaksesnya.
            queryClient.removeQueries({ queryKey: ['users', 'v4', 'detail', userId] });
        },

        /**
         * Callback yang dijalankan jika mutasi gagal.
         */
        onError: (_error) => {
            // Logging sudah ditangani di mutationFn.
        },
    });
};